import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";
import { HERO_INITIAL, HERO_ANIMATE, HERO_TRANSITION } from "./animations";

// Brand hero band — back link, logo, category, tagline, story paragraph, CTA
// on the left; featured deck-page image on the right.
export default function BrandHero({ brand, featured, inquiryHref }) {
  const { t } = useT();
  const navigate = useNavigate();
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: brand.palette.bg, color: brand.palette.text }}
    >
      {/* Ambient brand emblem watermark — applies to every brand hero */}
      {(brand.watermark || brand.logo) && (
        <img
          aria-hidden
          src={brand.watermark || brand.logo}
          alt=""
          className="pointer-events-none select-none absolute -right-32 top-1/2 -translate-y-1/2 w-[620px] max-w-none opacity-[0.08] rotate-6"
        />
      )}
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <button
            onClick={() => navigate("/#brands")}
            data-testid={BRAND_IDS.backHome}
            className="inline-flex items-center gap-2 text-sm font-medium opacity-80 hover:opacity-100 transition-opacity mb-8"
            style={{ color: brand.palette.text }}
          >
            <ArrowLeft className="w-4 h-4" /> {t("brandPage.backHome")}
          </button>
          {brand.logo && (
            <img
              src={brand.logo}
              alt={brand.name}
              className="h-20 md:h-28 w-auto object-contain mb-8"
            />
          )}
          <p
            className="text-xs md:text-sm font-semibold uppercase tracking-[0.28em] opacity-90"
            style={{ color: brand.palette.accent }}
          >
            {t(`brandContent.${brand.slug}.category`)}
          </p>
          <h1
            className="font-editorial italic mt-5 text-4xl md:text-6xl leading-[1.05]"
            style={{ color: brand.palette.text }}
          >
            &ldquo;{t(`brandContent.${brand.slug}.tagline`)}&rdquo;
          </h1>
          <p
            className="mt-8 text-base md:text-lg leading-relaxed max-w-xl"
            style={{ color: brand.palette.text, opacity: 0.9 }}
          >
            {brand.story && (t(`brandContent.${brand.slug}.story`) || brand.story)}
          </p>

          <Link
            to={inquiryHref}
            data-testid={BRAND_IDS.ctaInquiry}
            className="mt-10 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02]"
            style={{ background: brand.palette.accent, color: brand.palette.bg }}
          >
            {t("brandPage.partnerCta")}
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-full"
              style={{ background: brand.palette.text, color: brand.palette.accent }}
            >
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </Link>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center">
          <motion.img
            initial={HERO_INITIAL}
            animate={HERO_ANIMATE}
            transition={HERO_TRANSITION}
            src={featured || brand.image}
            alt={`${brand.name} — presentation`}
            data-testid={BRAND_IDS.deckImage(brand.slug, 0)}
            className="w-full h-auto max-h-[640px] object-contain rounded-3xl shadow-2xl"
          />
        </div>
      </div>
    </section>
  );
}
